// @ts-check

import {
  applyDrawingObjectState,
  canonicalDrawingObjectKey,
  drawingObjectCenter,
  drawingObjectState,
  getDrawingObject
} from '../../objects/drawing_object_registry.js?v=20260904f';
import { normalizeAngleDeg } from '../../shared/math.js?v=20260706d';
import { rotatePointAroundPivot } from '../geometry/rotation.js?v=20260706d';
import { boundsCenter, boundsFromState, normalizeBounds } from '../geometry/bounds.js?v=20260706d';

const rotationAdaptersByOwner = new Map();

function normalizeOwner(value) {
  return String(value || '').trim();
}

function ownerIdentity(value = {}) {
  if (typeof value === 'string') return { owner: normalizeOwner(value), valid: true };
  const owners = [...new Set([
    value?.systemType,
    value?.systemId,
    value?.systemKey,
    value?.system,
    value?.owner,
    value?.ownerId
  ].map(normalizeOwner).filter(Boolean))];
  return { owner: owners.length === 1 ? owners[0] : '', valid: owners.length <= 1 };
}

function ownerFromValue(value = {}) {
  return ownerIdentity(value).owner;
}

function warnAdapterHook(owner, hook, error) {
  console.warn(`2D-Rotationsadapter ${owner || '(ohne Owner)'}: ${hook} fehlgeschlagen.`, error);
}

function isolateUnexpectedThenable(owner, hook, value) {
  if (!value || (typeof value !== 'object' && typeof value !== 'function')) return false;
  let then;
  try {
    then = value.then;
  } catch (error) {
    warnAdapterHook(owner, `${hook}:thenable`, error);
    return true;
  }
  if (typeof then !== 'function') return false;
  try {
    Promise.resolve(value).then(
      () => undefined,
      error => warnAdapterHook(owner, `${hook}:thenable`, error)
    );
  } catch (error) {
    warnAdapterHook(owner, `${hook}:thenable`, error);
  }
  return true;
}

function markAdapterHookFailed(resolution, hook) {
  if (!resolution || typeof resolution !== 'object') return;
  try {
    resolution.hookFailed = true;
    resolution.failedHook = hook;
  } catch {
    // Fremde, unveränderliche Resolution-Objekte bleiben weiterhin fail-closed.
  }
}

export function groupRotationOwner(group = {}) {
  try {
    const owners = [
      group?.systemType,
      group?.systemId,
      group?.systemKey,
      group?.system,
      group?.owner,
      group?.ownerId,
      ...(group?.objects || []).flatMap(object => [
        object?.systemType,
        object?.systemId,
        object?.owner
      ])
    ].map(normalizeOwner).filter(Boolean);
    const uniqueOwners = [...new Set(owners)];
    return uniqueOwners.length === 1 ? uniqueOwners[0] : '';
  } catch (error) {
    warnAdapterHook('', 'Owner-Auflösung', error);
    return '';
  }
}

export function registerGroupRotationAdapter(ownerOrAdapter, optionalAdapter = null) {
  const adapter = optionalAdapter || (ownerOrAdapter && typeof ownerOrAdapter === 'object' ? ownerOrAdapter : null);
  let owner = typeof ownerOrAdapter === 'string' ? normalizeOwner(ownerOrAdapter) : '';
  try {
    const adapterOwner = ownerIdentity(adapter || {});
    owner ||= adapterOwner.owner;
    if (!owner || !adapter || typeof adapter !== 'object' || !adapterOwner.valid) return false;
    if (typeof adapter.rotate !== 'function') return false;
    const declaredOwner = adapterOwner.owner;
    if (declaredOwner && declaredOwner !== owner) return false;
  } catch (error) {
    warnAdapterHook(owner, 'Registrierung', error);
    return false;
  }
  if (rotationAdaptersByOwner.has(owner)) return false;
  rotationAdaptersByOwner.set(owner, adapter);
  return true;
}

export function unregisterGroupRotationAdapter(ownerOrAdapter, optionalAdapter = null) {
  try {
    const ownerIdentityValue = ownerIdentity(ownerOrAdapter || {});
    if (!ownerIdentityValue.valid) return false;
    const owner = typeof ownerOrAdapter === 'string'
      ? normalizeOwner(ownerOrAdapter)
      : ownerIdentityValue.owner;
    const adapter = optionalAdapter || (ownerOrAdapter && typeof ownerOrAdapter === 'object' ? ownerOrAdapter : null);
    if (!owner) return false;
    const registered = rotationAdaptersByOwner.get(owner);
    if (!registered || (adapter && registered !== adapter)) return false;
    return rotationAdaptersByOwner.delete(owner);
  } catch (error) {
    warnAdapterHook('', 'Abmeldung', error);
    return false;
  }
}

export function clearGroupRotationAdapters(owner = null) {
  let identity;
  try {
    identity = ownerIdentity(owner || {});
  } catch (error) {
    warnAdapterHook('', 'Bereinigung', error);
    return;
  }
  if (!identity.valid) return;
  const ownerId = identity.owner;
  if (ownerId) {
    rotationAdaptersByOwner.delete(ownerId);
    return;
  }
  rotationAdaptersByOwner.clear();
}

export function groupRotationAdapters() {
  return [...rotationAdaptersByOwner.values()];
}

function rotatedStatePosition(state, pivot, angleDeg) {
  const position = state?.position || state;
  if (!Number.isFinite(Number(position?.x)) || !Number.isFinite(Number(position?.z))) return null;
  const currentPosition = { x: Number(position.x), z: Number(position.z) };
  const stableBounds = normalizeBounds(state?.metadata?.localBounds) || boundsFromState(state);
  const center = drawingObjectCenter(state) || boundsCenter(stableBounds) || currentPosition;
  const offset = {
    x: center.x - currentPosition.x,
    z: center.z - currentPosition.z
  };
  const nextCenter = rotatePointAroundPivot(center, pivot, angleDeg);
  return {
    x: nextCenter.x - offset.x,
    z: nextCenter.z - offset.z
  };
}

export function rotateDrawingObjectsWithDefaultAdapter(group, angleDeg = 90, pivot = null, options = {}) {
  const rotationDelta = Number(angleDeg) || 0;
  const owner = groupRotationOwner(group);
  if (!owner) return [];
  const objects = (group?.objects || []).filter(object => (
    object?.rotatable
    && ownerFromValue(object) === owner
  ));
  const baseStates = options.baseStates instanceof Map ? options.baseStates : null;
  const sharedPivot = pivot || null;
  const rotated = [];
  for (const object of objects) {
    const state = baseStates?.get(object)
      || baseStates?.get(canonicalDrawingObjectKey(owner, object.id))
      || (getDrawingObject(object.id) === object ? baseStates?.get(object.id) : null)
      || drawingObjectState(object);
    if (!state) throw new Error(`DrawingObject ${owner}:${object.id} liefert keinen synchronen Zustand.`);
    const baseRotation = options.absolute === true ? 0 : Number(state.rotation) || 0;
    const nextState = {
      ...state,
      rotation: normalizeAngleDeg(baseRotation + rotationDelta),
      exactTransform: Boolean(sharedPivot && (objects.length > 1 || options.forcePivot === true || options.exactTransform === true))
    };
    if (sharedPivot && state.position && (objects.length > 1 || options.forcePivot === true)) {
      const nextPosition = rotatedStatePosition(state, sharedPivot, rotationDelta);
      if (!nextPosition) throw new Error(`DrawingObject ${owner}:${object.id} liefert keine drehbare Position.`);
      nextState.position = nextPosition;
      nextState.groupRotationPivot = sharedPivot;
      nextState.exactTransform = true;
    }
    if (!applyDrawingObjectState(object, nextState)) {
      throw new Error(`DrawingObject ${owner}:${object.id} lehnt den synchronen Rotationszustand ab.`);
    }
    rotated.push({ object, state: nextState });
  }
  return rotated;
}

export function resolveGroupRotationAdapter(group, state = null) {
  const owner = groupRotationOwner(group);
  if (!owner) return { owner: '', adapter: null, valid: false, hookFailed: false };
  const adapter = rotationAdaptersByOwner.get(owner) || null;
  if (!adapter) return { owner, adapter: null, valid: true, hookFailed: false };
  try {
    if (typeof adapter.supports === 'function') {
      const result = adapter.supports(group, state);
      if (isolateUnexpectedThenable(owner, 'supports', result)) {
        return { owner, adapter: null, valid: false, hookFailed: true, failedHook: 'supports' };
      }
      if (result === false) return { owner, adapter: null, valid: true, hookFailed: false };
    }
  } catch (error) {
    warnAdapterHook(owner, 'supports', error);
    return { owner, adapter: null, valid: false, hookFailed: true, failedHook: 'supports' };
  }
  return { owner, adapter, valid: true, hookFailed: false };
}

export function adapterForGroupRotation(group, state = null) {
  const resolution = resolveGroupRotationAdapter(group, state);
  return resolution.valid ? resolution.adapter : null;
}

export function canRotateGroupWithAdapter(group, state = null, resolution = null) {
  const resolved = resolution || resolveGroupRotationAdapter(group, state);
  if (!resolved.valid) return false;
  const adapter = resolved.adapter;
  try {
    if (!adapter || typeof adapter.canRotate !== 'function') return true;
    const result = adapter.canRotate(group, state);
    if (isolateUnexpectedThenable(resolved.owner, 'canRotate', result)) {
      markAdapterHookFailed(resolved, 'canRotate');
      return false;
    }
    return result !== false;
  } catch (error) {
    markAdapterHookFailed(resolved, 'canRotate');
    warnAdapterHook(resolved.owner, 'canRotate', error);
    return false;
  }
}

export function invokeGroupRotationAdapter(resolution, group, angleDeg, pivot, state = null, options = {}) {
  const resolved = resolution || resolveGroupRotationAdapter(group, state);
  const adapter = resolved?.adapter;
  if (!resolved?.valid || !adapter) return { ok: false, result: [] };
  try {
    const result = adapter.rotate(group, angleDeg, pivot, state, options);
    if (isolateUnexpectedThenable(resolved.owner, 'rotate', result)) {
      markAdapterHookFailed(resolved, 'rotate');
      return { ok: false, result: [] };
    }
    return { ok: true, result };
  } catch (error) {
    markAdapterHookFailed(resolved, 'rotate');
    warnAdapterHook(resolved.owner, 'rotate', error);
    return { ok: false, result: [] };
  }
}
